import { Component, ElementRef, HostListener, OnInit, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { SettingsService } from '../services/settings.service';
import { ThemeMode, ThemeService } from '../services/theme.service';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, RouterOutlet],
  template: `
    <div class="min-h-screen flex flex-col print:block print:bg-white">
      <header class="bg-surface/90 backdrop-blur border-b border-line print:hidden sticky top-0 z-20">
        <div class="app-rail"></div>
        <div class="h-20 flex items-center justify-between px-6">
          <div class="flex items-center gap-3">
            <img src="logo-mark.png" alt="" class="w-12 h-12 app-logo-mark" />
            <span class="font-semibold text-ink text-xl tracking-tight">{{ firmName || 'Retail Printing' }}</span>
          </div>

          <div class="flex items-center gap-5">
            <div class="theme-switch" role="radiogroup" aria-label="Theme">
              @for (opt of themeOptions; track opt.mode) {
                <button
                  type="button"
                  role="radio"
                  class="theme-switch-btn"
                  [class.theme-switch-btn-active]="theme.mode() === opt.mode"
                  [attr.aria-checked]="theme.mode() === opt.mode"
                  [attr.title]="opt.label"
                  [attr.aria-label]="opt.label"
                  (click)="theme.setMode(opt.mode)"
                >
                  @switch (opt.mode) {
                    @case ('light') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                        <circle cx="12" cy="12" r="4" />
                        <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
                      </svg>
                    }
                    @case ('dark') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                      </svg>
                    }
                    @case ('system') {
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                        <rect x="2" y="4" width="20" height="13" rx="2" />
                        <path d="M8 21h8M12 17v4" />
                      </svg>
                    }
                  }
                </button>
              }
            </div>
            <span class="text-sm text-muted">Hi, <span class="font-medium text-ink">{{ userName }}</span></span>
            <button (click)="signOut()" class="app-btn-secondary !py-1.5 !px-3 text-xs">Sign Out</button>
          </div>
        </div>

        <nav class="h-11 flex items-center gap-1 px-6 border-t border-line-soft relative">
          <!-- Staff users see only Add Order, Pending Orders and Orders History. Everything
               else is owner-only, matching the route guards. Hiding it is a convenience,
               not the boundary. -->
          @if (isOwner) {
            <a routerLink="/dashboard" routerLinkActive="nav-top-item-active" class="nav-top-item">Dashboard</a>

            <div class="relative">
              <button
                type="button"
                (click)="toggleMenu('settings', $event)"
                class="nav-top-item"
                [class.nav-top-item-active]="openMenu === 'settings'"
              >
                Settings / Setup
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>
              @if (openMenu === 'settings') {
                <div class="nav-dropdown">
                  <a routerLink="/settings/categories" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Manage Product Categories
                  </a>
                  <a routerLink="/settings/products" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Manage Products
                  </a>
                  <a routerLink="/settings/invoice" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Invoice Settings
                  </a>
                  <a routerLink="/settings/staff" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Staff Users
                  </a>
                </div>
              }
            </div>
          }

          <a routerLink="/orders/add" routerLinkActive="nav-top-item-active" class="nav-top-item">Add Order</a>
          <a routerLink="/orders/pending" routerLinkActive="nav-top-item-active" class="nav-top-item">Pending Orders</a>
          <a routerLink="/orders/history" routerLinkActive="nav-top-item-active" class="nav-top-item">Orders History</a>

          @if (isOwner) {
            <a routerLink="/orders/quotations" routerLinkActive="nav-top-item-active" class="nav-top-item">Quotations</a>

            <div class="relative">
              <button
                type="button"
                (click)="toggleMenu('expenses', $event)"
                class="nav-top-item"
                [class.nav-top-item-active]="openMenu === 'expenses'"
              >
                Expenses
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>
              @if (openMenu === 'expenses') {
                <div class="nav-dropdown">
                  <!-- The expense DASHBOARD is deliberately absent: it lives on
                       /dashboard behind that page's view switcher. -->
                  <a routerLink="/expenses" routerLinkActive="nav-dropdown-item-active" [routerLinkActiveOptions]="{ exact: true }" class="nav-dropdown-item" (click)="closeMenu()">
                    All Expenses
                  </a>
                  <a routerLink="/expenses/accounts" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Supplier Accounts
                  </a>
                </div>
              }
            </div>

            <div class="relative">
              <button
                type="button"
                (click)="toggleMenu('reports', $event)"
                class="nav-top-item"
                [class.nav-top-item-active]="openMenu === 'reports'"
              >
                Reports
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-3.5 h-3.5">
                  <polyline points="6,9 12,15 18,9" />
                </svg>
              </button>
              @if (openMenu === 'reports') {
                <div class="nav-dropdown">
                  <a routerLink="/reports/sales" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Sales Report
                  </a>
                  <a routerLink="/reports/account-summary" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Accounts Summary
                  </a>
                  <a routerLink="/reports/account-statement" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Customer Statement
                  </a>
                  <a routerLink="/reports/cash-collection" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Cash Collection Report
                  </a>
                  <a routerLink="/reports/dues" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Outstanding Dues
                  </a>
                  <a routerLink="/reports/orders-by-user" routerLinkActive="nav-dropdown-item-active" class="nav-dropdown-item" (click)="closeMenu()">
                    Orders By User
                  </a>
                </div>
              }
            </div>
          }
        </nav>
      </header>

      <main class="flex-1 p-6 print:p-0">
        <router-outlet />
      </main>
    </div>
  `,
  styles: [
    `
      .nav-top-item {
        position: relative;
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.5rem 0.75rem;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 600;
        color: var(--color-muted);
        transition: color 0.15s, background 0.15s;
      }
      .nav-top-item:hover {
        background: var(--overlay-hover);
        color: var(--color-ink);
      }
      .nav-top-item-active {
        color: var(--color-accent-text);
      }
      .nav-top-item-active::after {
        content: "";
        position: absolute;
        left: 0.75rem;
        right: 0.75rem;
        bottom: -0.0625rem;
        height: 2px;
        border-radius: 9999px;
        background-image: linear-gradient(90deg, #6366f1, #38bdf8);
      }
      .nav-dropdown {
        position: absolute;
        top: calc(100% + 0.375rem);
        left: 0;
        min-width: 15rem;
        background: var(--color-surface-2);
        border: 1px solid var(--color-line);
        border-radius: 0.75rem;
        box-shadow: var(--shadow-dropdown);
        padding: 0.375rem;
        z-index: 30;
      }
      .nav-dropdown-item {
        display: block;
        padding: 0.5rem 0.75rem;
        border-radius: 0.5rem;
        font-size: 0.875rem;
        font-weight: 500;
        color: var(--color-muted);
        transition: color 0.15s, background 0.15s;
      }
      .nav-dropdown-item:hover {
        background: linear-gradient(135deg, rgba(99, 102, 241, 0.14), rgba(56, 189, 248, 0.1));
        color: var(--color-ink);
      }
      .nav-dropdown-item-active {
        color: var(--color-accent-text);
        background: linear-gradient(135deg, rgba(99, 102, 241, 0.16), rgba(56, 189, 248, 0.12));
      }
      .theme-switch {
        display: inline-flex;
        gap: 0.125rem;
        padding: 0.1875rem;
        border-radius: 0.625rem;
        background: var(--color-surface-2);
        border: 1px solid var(--color-line);
      }
      .theme-switch-btn {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 1.875rem;
        height: 1.625rem;
        border-radius: 0.4375rem;
        color: var(--color-muted);
        transition: color 0.15s, background 0.15s;
      }
      .theme-switch-btn:hover:not(.theme-switch-btn-active) {
        background: var(--overlay-hover);
        color: var(--color-ink);
      }
      .theme-switch-btn-active {
        color: var(--color-accent-text);
        background: var(--color-surface);
        box-shadow: inset 0 0 0 1px rgba(99, 102, 241, 0.3);
      }
      .theme-switch-btn:focus-visible {
        outline: 2px solid #6366f1;
        outline-offset: 1px;
      }
    `,
  ],
})
export class ShellComponent implements OnInit {
  private settingsService = inject(SettingsService);
  private auth = inject(AuthService);
  private router = inject(Router);
  private elementRef = inject(ElementRef);
  readonly theme = inject(ThemeService);

  readonly themeOptions: { mode: ThemeMode; label: string }[] = [
    { mode: 'light', label: 'Light theme' },
    { mode: 'dark', label: 'Dark theme' },
    { mode: 'system', label: 'System default' },
  ];

  firmName = '';
  openMenu: 'settings' | 'reports' | 'expenses' | null = null;

  // The signed-in staff member's display name, for the header greeting.
  get userName(): string {
    return this.auth.user()?.name || 'there';
  }

  /** Whether to show the owner-only nav items. Cosmetic — see the guard. */
  get isOwner(): boolean {
    return this.auth.isOwner();
  }

  ngOnInit(): void {
    this.settingsService.getInvoiceSettings().subscribe({
      next: (s) => (this.firmName = s.firm_name),
      error: () => {
        // Cosmetic only — the firm name is just the header label, and the rest of the
        // app works without it, so fall back rather than blocking the shell.
        this.firmName = 'Shekhawat Printers';
      },
    });
  }

  toggleMenu(menu: 'settings' | 'reports' | 'expenses', event: Event): void {
    event.stopPropagation();
    this.openMenu = this.openMenu === menu ? null : menu;
  }

  closeMenu(): void {
    this.openMenu = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.openMenu = null;
    }
  }

  signOut(): void {
    this.auth.logout();
    this.router.navigateByUrl('/login');
  }
}
